import PropMapper from '@v/util/PropMapper.js'

export default class BookingDetailsApiModel {
  constructor(rawModel) {
    new PropMapper({ name: 'BookingDetailsApiModel', instance: this, rawModel })
      .mapObject('Booking', x => new BookingApiModel(x))
      .mapArray('BookingProducts', x => new BookingProductApiModel(x))
      .mapObject('BookingCosts', x => new BookingCostsOverviewApiModel(x))
      .mapArray('Customers', x => new CustomerApiModel(x))
      .mapArray('Remarks', x => new BookingRemarkApiModel(x))
      .mapArray('Visitors', x => new BookingVisitorApiModel(x))
      .mapArray('Payments', x => new PaymentApiModel(x))
      .mapArray('Mails', x => new BookingMailApiModel(x))
      .mapArray('ExternalPaymentMethods', x => new ExternalPaymentMethodLiteApiModel(x))
      .mapBool('DisplayPricesIncludingTax')
      .mapArray('IntegrationsInfo', x => new IntegrationInfoApiModel(x))
      .mapArray(
        'IntegrationFieldDefinitionsList',
        x => new IntegrationFieldDefinitionListApiModel(x),
      )
  }
}

class IntegrationFieldDefinitionListApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'IntegrationFieldDefinitionListApiModel',
      instance: this,
      rawModel,
    })
      .mapOneOf('IntegrationCode', integrationCodes)
      .mapString('IntegrationDisplayName')
      .mapArray('FieldDefinitions', x => x)
  }
}

const integrationCodes = [
  'NotSet',
  'Testing',
  'CardConnect',
  'Clubspeed',
  'ERP',
  'PublicApi',
  'Gifty',
  'Stripe',
  'StripeTest',
  'StripeExpress',
  'StripeExpressTest',
  'Till',
  'Voucherify',
  'Zapier',
  'Zendesk',
  'Innomeer',
  'ExternalPaymentMethod',
  'Briq',
  'Lightspeed',
  'LightspeedK',
  'Fast',
  'Entertainment501',
  'PrisonIsland',
  'Smartwaiver',
  'GiftUp',
  'GoogleCalendar',
  'XCube',
  'Chargebee',
  'UnTill',
  'ClubspeedTiming',
  'Paylogic',
  'Mailchimp',
  'MPlusKassa',
  'Widget',
  'POSPay',
  'Mice',
]

class IntegrationInfoApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'IntegrationInfoApiModel',
      instance: this,
      rawModel,
    })
      .mapOneOf('IdentificationCode', integrationCodes)
      .mapString('LocalizedName')
      .mapString('LocalizedDescription')
      .mapObject('Actions', x => x)
  }
}

// === Booking ===

class BookingApiModel {
  constructor(rawModel) {
    new PropMapper({ name: 'BookingApiModel', instance: this, rawModel })
      .mapString('Key')
      .mapString('BusinessUnitKey')
      .mapString('CreatedDatetime')
      .mapString('Number')
      .mapString('Reference')
      .mapNumber('AmountTotal')
      .mapNumber('AmountPaid')
      .mapString('NumberOfPersons')
      .mapArray('NumberOfPersonsPerSegment', x => new SegmentQuantityApiModel(x))
      .mapString('CurrentStatusCode')
      .mapOneOf('AttendanceStatus', [
        AttendanceStatus.Open,
        AttendanceStatus.CheckedIn,
        AttendanceStatus.NoShow,
      ])
      .mapString('AttendanceStatusDatetime')
      .mapString('StartTime')
      .mapString('EndTime')
      .mapString('PlannedCheckinTime')
      .mapString('ActualCheckinTime')
      .mapString('OnlineBookingCheckedDateTime')
      .mapObject('OnlineBookingCheckedUser', x => new UserInfoApiModel(x))
      .mapOneOf('Origin', ['NotSet', 'Admin', 'Online', 'Fast4', 'External', 'Clubspeed', 'Viator'])
      .mapOneOf('NextBillingMethod', ['None', 'ToBeDetermined', 'InvoiceNow', 'InvoiceAfter'])
      .mapOneOf('CurrentBillingMethod', ['None', 'ToBeDetermined', 'InvoiceNow', 'InvoiceAfter'])
      .mapString('CurrentBillingMethodDatetime')
      .mapString('ShoppingCartReservationDateTime')
      .mapArray('IntegrationFields', x => new IntegrationFieldApiModel(x))
  }
}

class IntegrationFieldApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'IntegrationFieldApiModel',
      instance: this,
      rawModel,
    })
      .mapOneOf('IntegrationCode', integrationCodes)
      .mapString('DisplayName')
      .mapArray('Fields', x => x)
  }
}

class SegmentQuantityApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'SegmentQuantityApiModel',
      instance: this,
      rawModel,
    })
      .mapString('ClassificationCode')
      .mapString('SegmentCode')
      .mapString('Name')
      .mapString('NamePlural')
      .mapString('Quantity')
  }
}

class BookingPersonsPerClassificationApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingPersonsPerClassificationApiModel',
      instance: this,
      rawModel,
    })
      .mapArray('NumberOfPersonsPerSegment', x => new BookingPersonsPerSegmentApiModel(x))
      .mapString('ClassificationCode')
  }
}

class BookingPersonsPerSegmentApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingPersonsPerSegmentApiModel',
      instance: this,
      rawModel,
    })
      .mapNumber('NumberOfPersons')
      .mapString('SegmentCode')
  }
}

class UserInfoApiModel {
  constructor(rawModel) {
    new PropMapper({ name: 'UserInfoApiModel', instance: this, rawModel })
      .mapString('UserKey')
      .mapString('Firstname')
      .mapString('Lastname')
      .mapString('Username')
      .mapString('ImageUrl')
      .mapString('BusinessUnitKey')
      .mapString('BusinessUnitDomain')
      .mapString('QualifiedUsername')
  }
}

// // === BookingProduct ===

class BookingProductApiModel {
  constructor(rawModel) {
    new PropMapper({ name: 'BookingProductApiModel', instance: this, rawModel })
      .mapString('Key')
      .mapString('Exclusivity')
      .mapString('CheckinDateTime')
      .mapString('FromDateTime')
      .mapString('ToDateTime')
      .mapString('CheckoutDateTime')
      .mapString('DealProductType')
      .mapString('ProductGroupProductKey')
      .mapString('ProductGroupName')
      .mapString('ProductName')
      .mapOneOf('ProductActiveState', ['Active', 'Inactive', 'Deleted'])
      .mapOneOf('ProductGroupActiveState', ['Active', 'Inactive', 'Deleted'])
      .mapString('ResourceName')
      .mapString('ResourceAbbreviation')
      .mapString('ResourceColor')
      .mapString('ProductQuantity')
      .mapArray('QuantitiesPerSegment', x => new SegmentQuantityApiModel(x))
      .mapObject('SegmentAvatar', x => new SegmentAvatarApiModel(x))
  }
}

class SegmentAvatarApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'SegmentAvatarApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Name')
      .mapString('ShortName')
      .mapString('Color')
      .mapBool('IsConflicted')
      .mapArray('ConflictingSegments', x => new BusinessUnitVisitorSegmentApiModel(x))
  }
}

class BusinessUnitVisitorSegmentApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BusinessUnitVisitorSegmentApiModel',
      instance: this,
      rawModel,
    })
      .mapBool('IsUsedByResources')
      .mapString('Code')
      .mapString('Name')
      .mapString('NamePlural')
      .mapString('Description')
      .mapString('Color')
      .mapBool('Enabled')
      .mapNumber('Ordering')
  }
}

// === BookingCosts ===

export class BookingCostsOverviewApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingCostsOverviewApiModel',
      instance: this,
      rawModel,
    })
      .mapArray('ProductGroups', x => new BookingDealCostsItemApiModel(x))
      .mapArray('BookingProducts', x => new BookingProductCostsItemApiModel(x))
      .mapArray('Discounts', x => new BookingDiscountApiModel(x))
      .mapArray('Vouchers', x => new BookingVoucherApiModel(x))
      .mapArray('TaxTotals', x => new BookingTaxTotalApiModel(x))
      .mapObject('Total', x => new PriceApiModel(x))
      .mapObject('PricePerPerson', x => new PriceApiModel(x))
      .mapNumber('NumberOfPersons')
      .mapObject('BookingFee', x => new PriceApiModel(x))
      .mapObject('MerchantFee', x => new PriceApiModel(x))
      .mapObject('ProcessorFee', x => new PriceApiModel(x))
  }
}

class BookingDealCostsItemApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingDealCostsItemApiModel',
      instance: this,
      rawModel,
    })
      .mapString('DisplayName')
      .mapNumber('Quantity')
      .mapObject('EstimatedPricePerPerson', x => new PriceApiModel(x))
      .mapObject('TotalPrice', x => new PriceApiModel(x))
      .mapArray('BookingProducts', x => new BookingProductCostsItemApiModel(x))
  }
}

class BookingProductCostsItemApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingProductCostsItemApiModel',
      instance: this,
      rawModel,
    })
      .mapString('ProductName')
      .mapString('SegmentName')
      .mapNumber('Quantity')
      .mapString('DealProductType')
      .mapOneOf('ProductActiveState', ['Active', 'Inactive', 'Deleted'])
      .mapObject('EstimatedPricePerPerson', x => new PriceApiModel(x))
      .mapObject('TotalPrice', x => new PriceApiModel(x))
  }
}

class BookingTaxTotalApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingTaxTotalApiModel',
      instance: this,
      rawModel,
    })
      .mapString('TaxKey')
      .mapNumber('Percentage')
      .mapString('TaxName')
      .mapObject('TotalBeforeDiscount', x => new PriceApiModel(x))
      .mapObject('Discount', x => new PriceApiModel(x))
      .mapObject('TotalAfterDiscount', x => new PriceApiModel(x))
  }
}

class PriceApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'PriceApiModel',
      instance: this,
      rawModel,
    })
      .mapNumber('IncludingTax')
      .mapNumber('ExcludingTax')
      .mapNumber('Tax')
      .mapNumber('TaxRate')
      .mapString('TaxName')
      .mapBool('DisplayIncludingTax')
  }
}

// === Discounts ===

export const DiscountType = Object.freeze({
  NotSet: 'NotSet',
  Price: 'Price',
  Percent: 'Percent',
})

export class BookingDiscountApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingDiscountApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Key')
      .mapOneOf('Type', [DiscountType.NotSet, DiscountType.Price, DiscountType.Percent])
      .mapNumber('Value')
      .mapBool('IncludesTax')
  }
}

// === Vouchers ===

export class BookingVoucherApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingVoucherApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Key')
      .mapString('VoucherProcessor') // This is an enum in swagger, but let's keep it a string here to be forward compatible
      .mapString('VoucherCode')
      .mapOneOf('DiscountType', ['NotSet', 'Discount', 'Payment'])
      .mapString('PaymentType')
      .mapNumber('VoucherDiscountPercentage')
      .mapNumber('CalculatedAmount')
      .mapString('RedemptionDatetimeUtc')
      .mapBool('IncludesTax')
  }
}

// === Customer ===

class CustomerApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'CustomerApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Key')
      .mapString('Firstname')
      .mapString('Lastname')
      .mapString('Nickname')
      .mapString('Gender')
      .mapString('Birthdate')
      .mapString('Email')
      .mapString('Street')
      .mapString('Housenumber')
      .mapString('Zipcode')
      .mapString('City')
      .mapString('CountryCode')
      .mapString('Mobilenumber')
      .mapString('Registrationdate')
      .mapString('RemovedOn')
      .mapBool('SingleUseAccount')
      .mapString('CompanyName')
      .mapString('CompanyEmail')
      .mapString('CompanyAddress')
      .mapString('CompanyZipcode')
      .mapString('CompanyCity')
      .mapString('CompanyCountryCode')
      .mapString('CompanyPhonenumber')
      .mapString('CompanyTaxNumber')
      .mapString('CompanyCocNumber')
      .mapString('CustomReference')
      .mapString('Newsletter')
      .mapString('FullName')
      .mapString('Culture')
      .mapOneOf('Role', [
        'None',
        'MainBooker',
        'SecondaryContact',
        'FinancialContact',
        'Bachelor',
        'BirthdayPerson',
      ])
  }
}

// === Remarks ===

export class BookingRemarkApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingRemarkApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Key')
      .mapOptionalProp('UserKey')
      .mapOptionalProp('CustomerKey')
      .mapOptionalProp('DatetimeUTC')
      .mapString('Remark')
      .mapBool('Private')
  }
}

// === Visitors ===

export class BookingVisitorApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingVisitorApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Key')
      .mapString('CustomerKey')
      .mapString('Firstname')
      .mapString('Lastname')
      .mapString('Nickname')
      .mapString('Email')
      .mapString('ExternalId')
      .mapArray('BookingProductKeys')
      .mapNumber('Position')
      .mapString('Phonenumber')
      .mapString('Note')
  }
}

// === Payments ===

export const PaymentStatus = Object.freeze({
  NotSet: 'NotSet',
  Success: 'Success',
  Pending: 'Pending',
  Failed: 'Failed',
})

export class PaymentApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'PaymentApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Key')
      .mapString('BusinessUnitKey')
      .mapString('BusinessUnitName')
      .mapString('CreateDatetimeUTC')
      .mapString('CreateDatetimeLocal')
      .mapString('CurrencyCode')
      .mapNumber('Amount')
      .mapOneOf('Status', [
        PaymentStatus.NotSet,
        PaymentStatus.Success,
        PaymentStatus.Pending,
        PaymentStatus.Failed,
      ])
      .mapString('StatusDatetimeUTC')
      .mapString('StatusDatetimeLocal')
      .mapString('Description')
      .mapNumber('PSPID')
      .mapString('PSPUniqueReference')
      .mapString('ClientIP')
      .mapBool('IsTest')
      .mapBool('IsSuccess')
      .mapBool('IsPending')
      .mapBool('IsFailed')
      .mapString('BookingNumber')
      .mapString('BookingKey')
      .mapString('Processor')
      .mapString('PaymentMethod')
      .mapString('ReturnURL')
      .mapNumber('RefundableAmount')
      .mapNumber('MinimumRefundAmount')
      .mapBool('IsRefundable')
      .mapString('VoucherCode')
      .mapObject('CreatingUser', x => new UserInfoApiModel(x))
  }
}

// === Mails ===

export const MailType = Object.freeze({
  Confirm: 'Confirm',
  ConfirmOffline: 'ConfirmOffline',
  Paid: 'Paid',
  Reminder: 'Reminder',
  Evaluation: 'Evaluation',
  Concept: 'Concept',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
})

export class BookingMailApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'BookingMailApiModel',
      instance: this,
      rawModel,
    })
      .mapOneOf('MailType', [
        MailType.Confirm,
        MailType.ConfirmOffline,
        MailType.Paid,
        MailType.Reminder,
        MailType.Evaluation,
        MailType.Concept,
        MailType.Expired,
        MailType.Cancelled,
        null,
      ])
      .mapString('Key')
      .mapString('DatetimeUTC')
      .mapString('To')
      .mapString('CC')
      .mapString('BCC')
      .mapString('Subject')
      .mapString('Body')
      .mapString('AttachmentPath')
      .mapBool('BodyRetrieved')
      .mapString('Status')
      .mapString('From')
      .mapString('MandrilResponse')
  }
}

// === ExternalPaymentMethods ===

export class ExternalPaymentMethodLiteApiModel {
  constructor(rawModel) {
    new PropMapper({
      name: 'ExternalPaymentMethodLiteApiModel',
      instance: this,
      rawModel,
    })
      .mapString('Name')
      .mapString('DisplayName')
      .mapBool('Refundable')
  }
}

// helper models

export const AttendanceStatus = Object.freeze({
  Open: 'Open',
  CheckedIn: 'CheckedIn',
  NoShow: 'NoShow',
})
